import React, { useState } from "react";
import { Button } from "../ui/Button";
import { GalleryOverlay } from "../ui/GalleryOverlay";
import { SectionBackgroundIcons } from "../ui/BackgroundIcons";

interface Hook2SectionProps {
  heading: string;
  productImages: string[];
  ctaText: string;
  onCtaClick: () => void;
}

export function Hook2Section({
  heading,
  productImages,
  ctaText,
  onCtaClick,
}: Hook2SectionProps) {
  const [galleryOpen, setGalleryOpen] = useState(false);

  return (
    <section className="bg-[#0a0a0a] py-12 relative">
      <SectionBackgroundIcons variant="featured" />
      <div className="relative z-10 max-w-5xl mx-auto px-6 w-full text-center">
        <div className="flex flex-col items-center text-center gap-8">
          {/* Separator */}
          <div className="w-12 h-px bg-[#a79c68]/80 mx-auto mb-2" />
          {/* Heading */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
            <span className="text-[#a79c68]">Gallery</span>
          </h2>

          {/* Hero image with gallery trigger */}
          <div
            className="relative w-full md:aspect-[16/11] rounded-xl overflow-hidden cursor-pointer group"
            onClick={() => setGalleryOpen(true)}
          >
            <img
              src={productImages[0]}
              alt="Featured product"
              className="w-full h-full object-cover object-center transition-transform group-hover:scale-105"
            />
            {/* Overlay with gallery icon */}
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
              <div className="flex flex-col items-center gap-2 text-white">
                <svg
                  className="w-12 h-12"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
                  <path d="M14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z" />
                  <path d="M4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2z" />
                  <path d="M14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                <span className="text-lg font-semibold tracking-wide">View Gallery</span>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="flex justify-center mt-6">
            <Button variant="primary" size="lg" onClick={onCtaClick}>
              {ctaText}
            </Button>
          </div>
        </div>
      </div>

      {/* Gallery Overlay */}
      <GalleryOverlay
        images={productImages}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
      />
    </section>
  );
}
